import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import caseStates from "@_configs/caseStates.config";
import emailFlowStates from "@_configs/emailFlowStates.config";
import accountStates from "@_configs/accountStates.config";
import settlementPortalStates from "@_configs/settlementPortalStates.config";

import { sidebarActions } from "@_actions/sidebar.actions";
import { templateActions } from "@_actions/template.actions";
import { getUserDataFromJwt } from "@_utils/helpers";

import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Collapse from "@mui/material/Collapse";
import SettingsIcon from "@mui/icons-material/Settings";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import BusinessIcon from "@mui/icons-material/Business";
import ImportantDevicesIcon from "@mui/icons-material/ImportantDevices";

import styles from "./Sidebar.module.css";
import {
  AssessmentOutlined,
  BarChartOutlined,
  EmailOutlined,
  FileCopyOutlined,
} from "@mui/icons-material";
import SvgIcon from "@mui/material/SvgIcon";
import { userConstants } from "@_constants/user.constants";
import { caseConstants } from "@_constants/case.constants";
import { casesActions } from "@_actions/cases.actions";
import { customerActions } from "@_actions/customer.actions";
import { sidebarConstants } from "@_constants/sidebar.constants";

function Sidebar() {
  const jwtData = getUserDataFromJwt();
  const history = useHistory();

  const dispatch = useDispatch();
  const sidebar = useSelector((state) => state.sidebarReducer);
  const emails = useSelector((state) => state.emailsReducer.emails);
  const casesReducer = useSelector((state) => state.casesReducer);

  const [open, setOpen] = useState(false);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [openAccount, setOpenAccount] = useState(false);
  const [openSettlementPortal, setOpenSettlementPortal] = useState(false);
  const [isReportSectionOpen, setIsReportSectionOpen] = useState(false);

  useEffect(() => {
    dispatch(casesActions.getCasesCount());
    dispatch(customerActions.getCredits());
  }, [dispatch]);

  function listItemClicked(state) {
    dispatch(sidebarActions.updateStatus(state));
    if (state !== "EMAIL_FLOWS") {
      dispatch(templateActions.selectTemplate(null));
    }
    // Add navigation only for accounting menus that have dedicated routes
    switch (state) {
      case "PAYMENT_REPORTS":
        history.push("/accounting/payments");
        break;
      case "INVOICE_MANAGEMENT":
        history.push("/accounting/invoices");
        break;
      case "INBOUNDEMAILS":
        history.push("/emails/unmapped");
        break;
      case sidebarConstants.EMAIL_SETUP:
        history.push("/admin/account/email_setup");
        break;
      case sidebarConstants.CREDITS:
        history.push("/admin/account/credits");
        break;
      case sidebarConstants.PAYMENT_SETUP:
        history.push("/admin/account/payment_setup");
        break;
      case sidebarConstants.SUBSCRIPTION:
        history.push("/admin/account/subscription");
        break;
      case sidebarConstants.BUSINESS_DETAILS:
        history.push("/admin/account/business_details");
        break;
      case sidebarConstants.USERS:
        history.push("/admin/account/users");
        break;
      case sidebarConstants.BASE_SETTINGS:
        history.push("/admin/settlement_portal/base_settings/portal_address");
        break;
      case sidebarConstants.TRANSLATIONS:
        history.push("/admin/settlement_portal/translations");
        break;
      case sidebarConstants.INVOICE_TEMPLATES:
        history.push("/templates/invoices");
        break;
      case sidebarConstants.CASETEMPLATES:
        history.push("/templates/case_auto_fill");
        break;
      case sidebarConstants.DASHBOARD:
        history.push("/dashboard");
        break;
      case caseConstants.CASE_STATE_STANDBY:
        history.push("/cases/standby");
        break;
      case caseConstants.CASE_STATE_ACTIVE:
        history.push("/cases/active");
        break;
      case caseConstants.CASE_STATE_CANCELLED:
        history.push("/cases/cancelled");
        break;
      case caseConstants.CASE_STATE_PAYMENT_PROCESSING:
        history.push("/cases/payment_processing");
        break;
      case caseConstants.CASE_STATE_PAID:
        history.push("/cases/paid");
        break;
      case caseConstants.CASE_STATE_VALID_LICENSE:
        history.push("/cases/valid_license");
        break;
      case "CASES":
        history.push("/cases");
        break;
      case sidebarConstants.EMAIL_FLOWS:
        history.push("/templates/email_flows");
        break;
      default:
        // For all other states, navigate to Cases page
        history.push("/");
        break;
    }
  }

  function handleClick() {
    setOpen(!open);
  }

  function handleTemplateNavClick() {
    setTemplatesOpen(!templatesOpen);
  }

  function handleInboundEmailNavClick(state) {
    listItemClicked(state);
  }

  function handleClickReports() {
    setIsReportSectionOpen(!isReportSectionOpen);
  }

  function handleClickAccount() {
    setOpenAccount(!openAccount);
  }

  function handleClickSettlementPortal() {
    setOpenSettlementPortal(!openSettlementPortal);
  }

  const isAccountingSubsectionSelected = () => {
    return (
      sidebar.status === sidebarConstants.INVOICE_MANAGEMENT ||
      sidebar.status === sidebarConstants.PAYMENT_REPORTS
    );
  };

  const isAccountSubsectionSelected = () => {
    return (
      sidebar.status === sidebarConstants.EMAIL_SETUP ||
      sidebar.status === sidebarConstants.CREDITS ||
      sidebar.status === sidebarConstants.PAYMENT_SETUP ||
      sidebar.status === sidebarConstants.SUBSCRIPTION ||
      sidebar.status === sidebarConstants.BUSINESS_DETAILS ||
      sidebar.status === sidebarConstants.USERS
    );
  };

  const isTemplatesSelected = () => {
    return (
      sidebar.status === sidebarConstants.EMAIL_FLOWS ||
      sidebar.status === sidebarConstants.INVOICE_TEMPLATES ||
      sidebar.status === sidebarConstants.CASETEMPLATES
    );
  };

  const isSettlementPortalSubsectionSelected = () => {
    return (
      sidebar.status === sidebarConstants.BASE_SETTINGS ||
      sidebar.status === sidebarConstants.TRANSLATIONS
    );
  };

  return (
    <div className={styles.sidebar}>
      <div className={styles.navbar}>
        {/* Case section */}
        <List component="nav">
          <ListItemButton
            key={"dashboard_key"}
            sx={{ curosr: "pointer" }}
            selected={sidebar.status === "DASHBOARD"}
            onClick={() => listItemClicked("DASHBOARD")}
            className={styles.cases_item}
            classes={{
              root: styles.list_item_button,
              selected: styles.selected,
            }}
          >
            <div className={styles.settings_icon}>
              <BarChartOutlined />
            </div>
            <div className={styles.row_container}>
              <div className={styles.cases_title}>Dashboard</div>
            </div>
          </ListItemButton>
          {Object.keys(caseStates).map((state, index) => {
            const selected = sidebar.status === state;
            const stateObj = caseStates[state];
            const isCases = stateObj.key === "CASES";

            return (
              <ListItemButton
                key={index}
                selected={selected}
                onClick={() => listItemClicked(stateObj.key)}
                sx={{ cursor: "pointer" }}
                className={isCases ? styles.cases_item : styles.status_item}
                classes={{
                  root: styles.list_item_button,
                  selected: styles.selected,
                }}
              >
                <div
                  className={isCases ? styles.cases_icon : styles.status_icon}
                >
                  {stateObj.icon}
                </div>

                <div className={styles.row_container}>
                  <div
                    className={
                      isCases ? styles.cases_title : styles.status_title
                    }
                  >
                    {stateObj.name}
                  </div>
                  <div className={styles.count}>
                    {
                      casesReducer.cases_count[
                        stateObj.key === "CASES" ? "all" : stateObj.key
                      ]
                    }
                  </div>
                </div>
              </ListItemButton>
            );
          })}

          {/* Inbound Email section */}
          {emails.length > 0 && (
            <ListItemButton
              selected={sidebar.status === "INBOUNDEMAILS"}
              sx={{ cursor: "pointer" }}
              classes={{
                root: styles.list_item_button,
                selected: styles.selected,
              }}
              className={styles.cases_item}
              onClick={() => handleInboundEmailNavClick("INBOUNDEMAILS")}
            >
              <EmailOutlined className={styles.settings_icon} />
              <div className={styles.row_container}>
                <div className={styles.cases_title}>Unmapped Emails</div>
                {emails.filter((email) => email.unread === 1).length > 0 && (
                  <div className={styles.count}>
                    {emails.filter((email) => email.unread === 1).length}
                  </div>
                )}
              </div>
            </ListItemButton>
          )}

          {/* Email flows section */}
          <ListItemButton
            sx={{ cursor: "pointer" }}
            classes={{
              root: styles.list_item_button,
              selected: styles.selected,
            }}
            className={styles.cases_item}
            onClick={handleTemplateNavClick}
          >
            <SvgIcon
              className={styles.settings_icon}
              children={
                <>
                  <path d="M5 5h2v3h10V5h2v5h2V5c0-1.1-.9-2-2-2h-4.18C14.4 1.84 13.3 1 12 1s-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h6v-2H5V5zm7-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1z"></path>
                  <path d="M21 11.5 15.51 17l-3.01-3-1.5 1.5 4.51 4.5 6.99-7z"></path>
                </>
              }
            />
            <div className={styles.row_container}>
              <div className={styles.cases_title}>Templates</div>
            </div>
            {templatesOpen || isTemplatesSelected() ? (
              <ExpandLess />
            ) : (
              <ExpandMore />
            )}
          </ListItemButton>

          <Collapse
            in={templatesOpen || isTemplatesSelected()}
            timeout="auto"
            unmountOnExit
          >
            <div>
              <List component="div" disablePadding>
                {Object.keys(emailFlowStates).map((state, index) => {
                  const selected = sidebar.status === state;
                  const stateObj = emailFlowStates[state];

                  return (
                    <div
                      className={styles.email_flows_agent}
                      key={stateObj.key}
                    >
                      <ListItemButton
                        selected={selected}
                        onClick={() => listItemClicked(stateObj.key)}
                        sx={{ cursor: "pointer" }}
                        className={styles.status_item}
                        classes={{
                          root: styles.list_item_button,
                          selected: styles.selected,
                        }}
                      >
                        <div className={styles.status_icon}>
                          <EmailOutlined />
                        </div>

                        <div className={styles.row_container}>
                          <div className={styles.status_title}>
                            {stateObj.name}
                          </div>
                        </div>
                      </ListItemButton>
                    </div>
                  );
                })}
              </List>

              <List component="div" disablePadding>
                <div
                  className={styles.email_flows_agent}
                  key={"Case Auto Fill"}
                >
                  <ListItemButton
                    selected={sidebar.status === "CASETEMPLATES"}
                    onClick={() => listItemClicked("CASETEMPLATES")}
                    sx={{ cursor: "pointer" }}
                    className={styles.status_item}
                    classes={{
                      root: styles.list_item_button,
                      selected: styles.selected,
                    }}
                  >
                    <div className={styles.status_icon}>
                      <SvgIcon
                        children={
                          <path d="M14 2H4c-1.1 0-2 .9-2 2v10h2V4h10V2zm4 4H8c-1.1 0-2 .9-2 2v10h2V8h10V6zm2 4h-8c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2zm0 10h-8v-8h8v8z"></path>
                        }
                      />
                    </div>

                    <div className={styles.row_container}>
                      <div className={styles.status_title}>Case Auto Fill</div>
                    </div>
                  </ListItemButton>
                </div>
              </List>
              {jwtData.subscription !==
                userConstants.USER_SUBSCRIPTION_LITE && (
                <List component="div" disablePadding>
                  <div
                    className={styles.email_flows_agent}
                    key={"Invoice Templates"}
                  >
                    <ListItemButton
                      selected={sidebar.status === "INVOICETEMPLATES"}
                      onClick={() => listItemClicked("INVOICETEMPLATES")}
                      sx={{ cursor: "pointer" }}
                      className={styles.status_item}
                      classes={{
                        root: styles.list_item_button,
                        selected: styles.selected,
                      }}
                    >
                      <div className={styles.status_icon}>
                        <SvgIcon
                          children={
                            <path d="M14 2H4c-1.1 0-2 .9-2 2v10h2V4h10V2zm4 4H8c-1.1 0-2 .9-2 2v10h2V8h10V6zm2 4h-8c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2v-8c0-1.1-.9-2-2-2zm0 10h-8v-8h8v8z"></path>
                          }
                        />
                      </div>

                      <div className={styles.row_container}>
                        <div className={styles.status_title}>Invoices</div>
                      </div>
                    </ListItemButton>
                  </div>
                </List>
              )}
            </div>
          </Collapse>

          {/* Reports */}
          <div>
            <ListItemButton
              key={"reports_key"}
              sx={{ cursor: "pointer" }}
              classes={{
                root: styles.list_item_button,
                selected: styles.selected,
              }}
              className={styles.cases_item}
              onClick={handleClickReports}
            >
              <AssessmentOutlined className={styles.settings_icon} />
              <div className={styles.row_container}>
                <div className={styles.cases_title}>Accounting</div>
              </div>
              {isReportSectionOpen || isAccountingSubsectionSelected() ? (
                <ExpandLess />
              ) : (
                <ExpandMore />
              )}
            </ListItemButton>

            <Collapse
              in={isReportSectionOpen || isAccountingSubsectionSelected()}
              timeout="auto"
              unmountOnExit
            >
              <div className={styles.settings_nav}>
                <List component="div" disablePadding>
                  <div className={styles.reports_agent} key={"paymentReports"}>
                    <ListItemButton
                      key={"payments_key"}
                      selected={sidebar.status === "PAYMENT_REPORTS"}
                      onClick={() => listItemClicked("PAYMENT_REPORTS")}
                      sx={{ cursor: "pointer" }}
                      className={styles.status_item}
                      classes={{
                        root: styles.list_item_button,
                        selected: styles.selected,
                      }}
                    >
                      <div className={styles.status_icon}>
                        <SvgIcon
                          children={
                            <path d="M19 14V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zm-2 0H3V6h14v8zm-7-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm13 0v11c0 1.1-.9 2-2 2H4v-2h17V7h2z"></path>
                          }
                        />
                      </div>

                      <div className={styles.row_container}>
                        <div className={styles.status_title}>Payments</div>
                      </div>
                    </ListItemButton>
                  </div>
                </List>
                <List component="div" disablePadding>
                  <div className={styles.reports_agent} key={"paymentReports"}>
                    <ListItemButton
                      key={"invoice_management_key"}
                      selected={sidebar.status === "INVOICE_MANAGEMENT"}
                      onClick={() => listItemClicked("INVOICE_MANAGEMENT")}
                      sx={{ cursor: "pointer" }}
                      className={styles.status_item}
                      classes={{
                        root: styles.list_item_button,
                        selected: styles.selected,
                      }}
                    >
                      <div className={styles.status_icon}>
                        <FileCopyOutlined />
                      </div>

                      <div className={styles.row_container}>
                        <div className={styles.status_title}>Invoices</div>
                      </div>
                    </ListItemButton>
                  </div>
                </List>
              </div>
            </Collapse>
          </div>

          {/* Display the setting section just for admin role */}
          {jwtData.user_role === "ADMIN" && (
            <div className={styles.settings_nav}>
              <ListItemButton
                sx={{ cursor: "pointer" }}
                classes={{
                  root: styles.list_item_button,
                  selected: styles.selected,
                }}
                className={styles.cases_item}
                onClick={handleClick}
              >
                <SettingsIcon className={styles.settings_icon} />
                <div className={styles.row_container}>
                  <div className={styles.cases_title}>Admin</div>
                </div>
                {open ||
                isAccountSubsectionSelected() ||
                isSettlementPortalSubsectionSelected() ? (
                  <ExpandLess />
                ) : (
                  <ExpandMore />
                )}
              </ListItemButton>

              <Collapse
                in={
                  open ||
                  isAccountSubsectionSelected() ||
                  isSettlementPortalSubsectionSelected()
                }
                timeout="auto"
                unmountOnExit
              >
                <div className={styles.settings_container}>
                  <List component="div" disablePadding>
                    <ListItemButton
                      sx={{ cursor: "pointer" }}
                      classes={{
                        root: styles.list_item_button,
                        selected: styles.selected,
                      }}
                      className={styles.cases_item}
                      onClick={handleClickAccount}
                    >
                      <div className={styles.status_icon}>
                        <BusinessIcon />
                      </div>
                      <div className={styles.row_container}>
                        <div className={styles.status_title}>Account</div>
                      </div>
                      {openAccount || isAccountSubsectionSelected() ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </ListItemButton>

                    <Collapse
                      in={openAccount || isAccountSubsectionSelected()}
                      timeout="auto"
                      unmountOnExit
                    >
                      {Object.keys(accountStates).map((state, index) => {
                        const selected = sidebar.status === state;
                        const stateObj = accountStates[state];
                        return (
                          ((stateObj.key === accountStates.CREDITS.key &&
                            jwtData.subscription ===
                              userConstants.USER_SUBSCRIPTION_ENTERPRISE) ||
                            stateObj.key !== accountStates.CREDITS.key) && (
                            <ListItemButton
                              key={index}
                              selected={selected}
                              onClick={() => listItemClicked(stateObj.key)}
                              sx={{ cursor: "pointer" }}
                              className={styles.status_item}
                              classes={{
                                root: styles.list_item_button,
                                selected: styles.selected,
                              }}
                            >
                              <div
                                className={
                                  stateObj.icon
                                    ? styles.status_icon
                                    : styles.status_no_icon
                                }
                              >
                                {stateObj.icon}
                              </div>

                              <div className={styles.row_container}>
                                <div className={styles.status_title}>
                                  {stateObj.name}
                                </div>
                              </div>
                            </ListItemButton>
                          )
                        );
                      })}
                    </Collapse>

                    <ListItemButton
                      sx={{ cursor: "pointer" }}
                      classes={{
                        root: styles.list_item_button,
                        selected: styles.selected,
                      }}
                      className={styles.cases_item}
                      onClick={handleClickSettlementPortal}
                    >
                      <div className={styles.status_icon}>
                        {" "}
                        <ImportantDevicesIcon />
                      </div>
                      <div className={styles.row_container}>
                        <div className={styles.status_title}>
                          Settlement Portal
                        </div>
                      </div>
                      {openSettlementPortal ||
                      isSettlementPortalSubsectionSelected() ? (
                        <ExpandLess />
                      ) : (
                        <ExpandMore />
                      )}
                    </ListItemButton>

                    <Collapse
                      in={
                        openSettlementPortal ||
                        isSettlementPortalSubsectionSelected()
                      }
                      timeout="auto"
                      unmountOnExit
                    >
                      {Object.keys(settlementPortalStates).map(
                        (state, index) => {
                          const selected = sidebar.status === state;
                          const stateObj = settlementPortalStates[state];
                          return (
                            <ListItemButton
                              key={index}
                              selected={selected}
                              onClick={() => listItemClicked(stateObj.key)}
                              sx={{ cursor: "pointer" }}
                              className={styles.status_item}
                              classes={{
                                root: styles.list_item_button,
                                selected: styles.selected,
                              }}
                            >
                              <div
                                className={
                                  stateObj.icon
                                    ? styles.status_icon
                                    : styles.status_no_icon
                                }
                              >
                                {stateObj.icon}
                              </div>

                              <div className={styles.row_container}>
                                <div className={styles.status_title}>
                                  {stateObj.name}
                                </div>
                              </div>
                            </ListItemButton>
                          );
                        },
                      )}{" "}
                    </Collapse>
                  </List>
                </div>
              </Collapse>
            </div>
          )}
        </List>
      </div>
    </div>
  );
}

export default Sidebar;
